/**
 * aero-component-cart-page.js
 * Live cart quantity updates on the cart page — no "Update" click required.
 * Loaded only via snippets/aero-component-cart-page.liquid.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Update the header cart badge.
   *
   * SURGICAL — find-or-create the badge, never rewrite the link's innerHTML.
   * A destructive rewrite would strip the cart-drawer open attributes
   * (data-air-cart-drawer-open / aria-controls / aria-expanded) that
   * sections/aero-header.liquid renders, silently breaking the drawer trigger,
   * and would delete the badge node the B2B catalog page holds a reference to.
   *
   * Empty carts set '' (not '0') so the DS :empty rule auto-hides the badge.
   */
  function updateCartBubble(itemCount) {
    var cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon) return;

    var badge = cartIcon.querySelector('.air-badge--count');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'air-badge air-badge--count';
      badge.setAttribute('aria-hidden', 'true');
      cartIcon.appendChild(badge);
    }
    badge.textContent = itemCount > 0 ? itemCount : '';

    var srCount = cartIcon.querySelector('.air-sr-only');
    if (itemCount > 0) {
      if (!srCount) {
        srCount = document.createElement('span');
        srCount.className = 'air-sr-only';
        cartIcon.appendChild(srCount);
      }
      srCount.textContent = itemCount + (itemCount === 1 ? ' item in cart' : ' items in cart');
    } else if (srCount) {
      srCount.remove();
    }
  }

  function findCartItem(cart, itemKey) {
    if (!cart.items || !Array.isArray(cart.items)) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].key === itemKey) return cart.items[i];
    }
    return null;
  }

  function applyCartToPage(form, cart) {
    form.querySelectorAll('[data-aero-cart-item]').forEach(function (row) {
      var itemKey = row.getAttribute('data-cart-item-key');
      var item = findCartItem(cart, itemKey);
      var select = row.querySelector('[data-aero-cart-qty]');
      var lineTotals = row.querySelectorAll('[data-aero-cart-line-total]');

      if (!item) {
        row.remove();
        return;
      }

      if (select) {
        select.value = String(item.quantity);
        select.setAttribute('data-aero-cart-qty-prev', select.value);
        select.disabled = false;
      }

      if (lineTotals.length) {
        var formatted = formatMoney(item.final_line_price);
        lineTotals.forEach(function (el) {
          el.textContent = formatted;
        });
      }
    });

    var subtotalEl = form.querySelector('[data-aero-cart-subtotal]');
    if (subtotalEl && cart.items_subtotal_price != null) {
      subtotalEl.textContent = formatMoney(cart.items_subtotal_price);
    }

    var totalEl = form.querySelector('[data-aero-cart-total]');
    if (totalEl && cart.total_price != null) {
      totalEl.textContent = formatMoney(cart.total_price);
    }

    updateCartBubble(cart.item_count);
    form.removeAttribute('aria-busy');
  }

  /**
   * Attach the free-shipping discount code to the CART itself.
   *
   * The checkout button routes through /discount/<code>?redirect=/checkout
   * (snippets/aero-cart-checkout-url.liquid), where the code is applied by the act
   * of navigating. The accelerated (wallet) buttons rendered beside it never
   * traverse that URL — <shopify-accelerated-checkout-cart> starts its own checkout
   * session from the cart — so without this they check out with NO discount.
   *
   * POST /cart/update.js { discount: '<code>' } puts the code on the cart, so both
   * entry points inherit it. The code comes from Liquid via data-cart-discount
   * (snippets/aero-cart-discount-code.liquid), the same snippet the URL encodes, so
   * the two paths cannot disagree about which code applies.
   *
   * Fire-and-forget, once per page load: a failure must never block checkout, and
   * the regular button still applies the discount via its URL. Shopify decides
   * eligibility — both codes are segment-scoped in admin.
   */
  function attachDiscount(form) {
    var code = (form.getAttribute('data-cart-discount') || '').trim();
    if (!code) return;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code })
    }).catch(function () {
      /* Discount is a bonus, never a blocker. */
    });
  }

  function initCartPage(form) {
    attachDiscount(form);

    form.addEventListener('change', function (event) {
      var select = event.target.closest('[data-aero-cart-qty]');
      if (!select || !form.contains(select)) return;

      var row = select.closest('[data-aero-cart-item]');
      if (!row) return;

      var itemKey = row.getAttribute('data-cart-item-key');
      var quantity = parseInt(select.value, 10);
      if (!itemKey || !quantity || quantity < 1) return;

      // Rollback target for a failed change.
      //
      // This used to read `-prev` and then write the same value straight back,
      // leaving the attribute frozen at the page-load quantity: once 4 -> 6 had
      // succeeded, a failed 6 -> 8 reverted to 4 rather than 6. Capture the old
      // value, then record the new one so the NEXT change rolls back to this
      // one. (`-prev` is seeded for every select at init below and re-synced
      // from the server by applyCartToPage on success, so it is always set.)
      var previousValue = select.getAttribute('data-aero-cart-qty-prev') || String(quantity);
      select.setAttribute('data-aero-cart-qty-prev', String(quantity));
      select.disabled = true;
      form.setAttribute('aria-busy', 'true');

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: quantity })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) {
              var message = data.description || data.message || 'Could not update cart';
              throw new Error(message);
            }
            return data;
          });
        })
        .then(function (cart) {
          applyCartToPage(form, cart);
        })
        .catch(function (error) {
          console.error('Cart quantity update failed:', error);
          select.value = previousValue;
          // Restore the rollback target too — it was optimistically set to the
          // quantity the server just rejected, which would make the next failed
          // change revert to a value the cart never held.
          select.setAttribute('data-aero-cart-qty-prev', previousValue);
          select.disabled = false;
          form.removeAttribute('aria-busy');
        });
    });

    form.querySelectorAll('[data-aero-cart-qty]').forEach(function (select) {
      select.setAttribute('data-aero-cart-qty-prev', select.value);
    });
  }

  function onReady(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  onReady(function () {
    document.querySelectorAll('[data-aero-cart-page]').forEach(initCartPage);
  });
})();
